import { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import { authService } from '../../services/authService';

const pages = [
  { name: 'Accueil', path: '/' },
  { name: 'Entreprises', path: '/entreprises' },
  { name: 'Offres', path: '/offres' },
  { name: 'Statistiques', path: '/stats' }
];

const etudiantPages = [
  { name: 'Wishlist', path: '/wishlist' },
  { name: 'Mes candidatures', path: '/candidatures' },
  { name: 'Profil', path: '/profil' }
];

const adminPages = [
  { name: 'Étudiants', path: '/etudiants' },
  { name: 'Pilotes', path: '/pilotes' },
  { name: 'Administration', path: '/admin' }
];

const pilotePages = [
  { name: 'Étudiants', path: '/etudiants' },
  { name: 'Profil', path: '/profil' }
];

function Header() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { currentUser, userRole, setCurrentUser, setUserRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isAdmin = userRole === ROLES.ADMIN;
  const isEtudiant = userRole === ROLES.ETUDIANT;
  const isPilote = userRole === ROLES.PILOTE;

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleCloseNavMenu();
  };

  const handleLogoutClick = () => {
    handleCloseNavMenu();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      setLogoutDialogOpen(false);
      await authService.logout();
      
      // Mettre à jour le contexte
      setCurrentUser(null);
      setUserRole(null);
      
      // Rediriger vers la page de login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleLoginClick = () => {
    navigate('/login');
    handleCloseNavMenu();
  };

  const isPathActive = (pagePath) => {
    // Pour la page d'accueil
    if (pagePath === '/' && location.pathname === '/') {
      return true;
    }
    
    // Pour les autres pages, vérifie si le chemin actuel commence par le chemin de la page
    return location.pathname.startsWith(pagePath) && pagePath !== '/';
  };

  return (
    <>
      <AppBar 
        position="static"
        color="primary"
        sx={{ 
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      >
        <Container maxWidth="xl">
          {/* Statut utilisateur en haut à droite - version desktop */}
          {currentUser && !isMobile && (
            <Box sx={{ 
              position: 'absolute', 
              top: 32,
              right: 32,
              zIndex: 1
            }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                  px: 3,
                  py: 0.8,
                  borderRadius: 1,
                  fontSize: '1rem'
                }}
              >
                {isAdmin ? 'Administrateur' : isEtudiant ? 'Étudiant' : 'Pilote'}
              </Typography>
            </Box>
          )}

          {/* Statut utilisateur en haut à droite - version mobile */}
          {currentUser && isMobile && (
            <Box sx={{ 
              position: 'absolute', 
              top: 104,
              right: 16,
              zIndex: 1
            }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                  px: 2,
                  py: 0.6,
                  borderRadius: 1,
                  fontSize: '0.9rem'
                }}
              >
                {isAdmin ? 'Administrateur' : isEtudiant ? 'Étudiant' : 'Pilote'}
              </Typography>
            </Box>
          )}

          {/* Titre centré */}
          <Box
            sx={{
              py: 2,
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Typography
              variant="h4"
              component="span"
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 700,
                letterSpacing: '.2rem',
                color: 'primary.contrastText',
                cursor: 'pointer',
                '&:hover': { opacity: 0.9 }
              }}
              onClick={() => navigate('/')}
            >
              UP-STAGE
            </Typography>
          </Box>

          <Toolbar 
            disableGutters 
            sx={{ 
              minHeight: { xs: '56px', md: '64px' },
              justifyContent: 'center'
            }}
          >
            {/* Menu hamburger pour mobile */}
            {isMobile && (
              <Box sx={{ position: 'absolute', left: 0 }}>
                <IconButton
                  size="large"
                  aria-label="menu"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorElNav}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    '& .MuiPaper-root': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  {pages.map((page) => (
                    <MenuItem 
                      key={page.path} 
                      onClick={() => handleNavigate(page.path)}
                      selected={isPathActive(page.path)}
                    >
                      <Typography>{page.name}</Typography>
                    </MenuItem>
                  ))}
                  {isEtudiant && etudiantPages.map((page) => (
                    <MenuItem 
                      key={page.path}
                      onClick={() => handleNavigate(page.path)}
                      selected={isPathActive(page.path)}
                    >
                      <Typography>{page.name}</Typography>
                    </MenuItem>
                  ))}
                  {isAdmin && adminPages.map((page) => (
                    <MenuItem 
                      key={page.path}
                      onClick={() => handleNavigate(page.path)}
                      selected={isPathActive(page.path)}
                    >
                      <Typography>{page.name}</Typography>
                    </MenuItem>
                  ))}
                  {isPilote && pilotePages.map((page) => (
                    <MenuItem 
                      key={page.path}
                      onClick={() => handleNavigate(page.path)}
                      selected={isPathActive(page.path)}
                    >
                      <Typography>{page.name}</Typography>
                    </MenuItem>
                  ))}
                  <MenuItem 
                    onClick={currentUser ? handleLogoutClick : handleLoginClick}
                  >
                    <Typography>
                      {currentUser ? 'Déconnexion' : 'Connexion'}
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Navigation pour grands écrans */}
            {!isMobile && (
              <Stack 
                direction="row" 
                spacing={4}
                alignItems="center"
              >
                {pages.map((page) => (
                  <Button
                    key={page.path}
                    onClick={() => handleNavigate(page.path)}
                    sx={{
                      color: 'primary.contrastText',
                      fontSize: '1rem',
                      textTransform: 'none',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: isPathActive(page.path) ? '100%' : '0%',
                        height: '3px',
                        bgcolor: 'primary.contrastText',
                        transition: 'width 0.3s ease'
                      },
                      '&:hover::after': {
                        width: '100%'
                      }
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
                {isEtudiant && etudiantPages.map((page) => (
                  <Button
                    key={page.path}
                    onClick={() => handleNavigate(page.path)}
                    sx={{
                      color: 'primary.contrastText',
                      fontSize: '1rem',
                      textTransform: 'none',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: isPathActive(page.path) ? '100%' : '0%',
                        height: '3px',
                        bgcolor: 'primary.contrastText',
                        transition: 'width 0.3s ease'
                      },
                      '&:hover::after': {
                        width: '100%'
                      }
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
                {isAdmin && adminPages.map((page) => (
                  <Button
                    key={page.path}
                    onClick={() => handleNavigate(page.path)}
                    sx={{
                      color: 'primary.contrastText',
                      fontSize: '1rem',
                      textTransform: 'none',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: isPathActive(page.path) ? '100%' : '0%',
                        height: '3px',
                        bgcolor: 'primary.contrastText',
                        transition: 'width 0.3s ease'
                      },
                      '&:hover::after': {
                        width: '100%'
                      }
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
                {isPilote && pilotePages.map((page) => (
                  <Button
                    key={page.path}
                    onClick={() => handleNavigate(page.path)}
                    sx={{
                      color: 'primary.contrastText',
                      fontSize: '1rem',
                      textTransform: 'none',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: isPathActive(page.path) ? '100%' : '0%',
                        height: '3px',
                        bgcolor: 'primary.contrastText',
                        transition: 'width 0.3s ease'
                      },
                      '&:hover::after': {
                        width: '100%'
                      }
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    onClick={currentUser ? handleLogoutClick : handleLoginClick}
                    startIcon={currentUser ? null : <LoginIcon />}
                    sx={{
                      color: 'text.primary',
                      fontSize: '1rem',
                      textTransform: 'none',
                      bgcolor: 'rgba(0, 0, 0, 0.1)',
                      px: 2,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.2)'
                      }
                    }}
                  >
                    {currentUser ? 'Déconnexion' : 'Connexion'}
                  </Button>
                </Box>
              </Stack>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Dialog de confirmation de déconnexion */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          Confirmation de déconnexion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Êtes-vous sûr de vouloir vous déconnecter ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleLogoutCancel}
            color="primary"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleLogoutConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Déconnexion
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Header; 